import React, { useEffect, useRef, useState } from 'react';
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats, Html5QrcodeScanType } from 'html5-qrcode';

interface BarCodesProps {}

interface ScanResult {
  decodedText: string;
  timestamp: Date;
  format?: string;
}

const BarCodes: React.FC<BarCodesProps> = () => {
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    initializeScanner();

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
    };
  }, []);

  const initializeScanner = () => {
    if (scannerRef.current) {
      return; 
    }

   const config = {
  fps: 10,
  qrbox: { width: 300, height: 300 },
  aspectRatio: 1.0,
  disableFlip: false,
  formatsToSupport: [
    Html5QrcodeSupportedFormats.QR_CODE,
    Html5QrcodeSupportedFormats.DATA_MATRIX,
    Html5QrcodeSupportedFormats.EAN_13,
    Html5QrcodeSupportedFormats.EAN_8,
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODE_39,
  ],
  showTorchButtonIfSupported: true,
  supportedScanTypes: [
    Html5QrcodeScanType.SCAN_TYPE_CAMERA,
    Html5QrcodeScanType.SCAN_TYPE_FILE
  ],
};

    scannerRef.current = new Html5QrcodeScanner(
      "qr-reader", 
      config,
      false 
    );

    scannerRef.current.render(
      onScanSuccess,
      onScanFailure
    );

    setIsScanning(true);
  };


  const onScanSuccess = (decodedText: string, decodedResult: any) => {
    console.log('QR код отсканирован:', decodedText);
    
    const newResult: ScanResult = {
      decodedText,
      timestamp: new Date(),
      format: decodedResult.result?.format?.formatName || 'Unknown'
    };

    setScanResults(prev => [newResult, ...prev.slice(0, 9)]);
    setError('');


    if (isValidUrl(decodedText)) {
      const userConfirm = window.confirm(
        `Отсканирован URL: ${decodedText}\n\nОткрыть ссылку в новой вкладке?`
      );
      
      if (userConfirm) {
        window.open(decodedText, '_blank');
      }
    }
  };

  const onScanFailure = (error: any) => {
  };

  const isValidUrl = (str: string): boolean => {
    try {
      new URL(str);
      return str.startsWith('http://') || str.startsWith('https://');
    } catch {
      return false;
    }
  };


  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.clear();
      scannerRef.current = null;
      setIsScanning(false);
    }
  };


  const startScanning = () => {
    if (!scannerRef.current) {
      initializeScanner();
    }
  };

 
  const clearResults = () => {
    setScanResults([]);
  };


  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Скопировано в буфер обмена!');
    }).catch(err => {
      console.error('Ошибка копирования:', err);
    });
  };

  return (
    <div style={{ 
      maxWidth: '800px', 
      margin: '0 auto', 
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>
        QR/Штрихкод сканер
      </h2>

      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        {!isScanning ? (
          <button
            onClick={startScanning}
            style={{
              background: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
         	Начать сканирование
          </button>
        ) : (
          <button
            onClick={stopScanning}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Остановить сканирование
          </button>
        )}

        <button
          onClick={clearResults}
          style={{
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Очистить результаты
        </button>
      </div>

      <div 
        id="qr-reader" 
        style={{
          marginBottom: '30px',
          border: '2px solid #dee2e6',
          borderRadius: '10px',
          overflow: 'hidden'
        }}
      />

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          <strong>Ошибка:</strong> {error}
        </div>
      )}

      {scanResults.length > 0 && (
        <div>
          <h3>Результаты сканирования ({scanResults.length}):</h3>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {scanResults.map((result, index) => (
              <div
                key={index}
                style={{
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '10px'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '10px'
                }}>
                  <span style={{
                    background: '#007bff',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    {result.format}
                  </span>
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>
                    {result.timestamp.toLocaleString()}
                  </span>
                </div>

                <div style={{
                  background: 'white',
                  padding: '10px',
                  borderRadius: '4px',
                  wordBreak: 'break-all',
                  marginBottom: '10px',
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }}>
                  {result.decodedText}
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => copyToClipboard(result.decodedText)}
                    style={{
                      background: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      padding: '5px 10px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    📋 Копировать
                  </button>

                  {isValidUrl(result.decodedText) && (
                    <button
                      onClick={() => window.open(result.decodedText, '_blank')}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      🔗 Открыть ссылку
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{
        background: '#d4edda',
        color: '#155724',
        padding: '15px',
        borderRadius: '5px',
        marginTop: '20px'
      }}>
        <ul style={{ margin: '10px 0 0 20px' }}>
          <li>Нажмите "Начать сканирование" и разрешите доступ к камере</li>
          <li>Наведите камеру на QR-код или штрихкод</li>
          <li>Также можно загрузить изображение с кодом</li>
          <li>Результаты автоматически появятся ниже</li>
          <li>Поддерживаются QR, DataMatrix, EAN-13, EAN-8, CODE-128, CODE-39</li>
        </ul>
      </div>
    </div>
  );
};

export default BarCodes;